import apiClient from './api';
import { ServiceType, Currency } from '../types/api';

export interface SystemConfig {
  config_key: string;
  config_value: any;
  config_type: string;
  description?: string;
  is_active: boolean;
}

export interface PricingConfig {
  thb?: number;
  usd?: number;
}

export interface ReferralConfig {
  credit_amount: PricingConfig;
  discount_amount: PricingConfig;
}

export interface CryptoPricingConfig {
  standard: PricingConfig;
  premium: PricingConfig;
}

export interface FallbackPricingConfig {
  standard: PricingConfig;
  premium: PricingConfig;
}

class ConfigService {
  private configCache: Map<string, any> = new Map();
  private cacheExpiry: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get configuration value by key with caching
   */
  private async getConfig<T = any>(key: string): Promise<T | null> {
    // Check cache first
    const cached = this.configCache.get(key);
    const expiry = this.cacheExpiry.get(key);
    
    if (cached && expiry && Date.now() < expiry) {
      return cached;
    }

    try {
      const response = await apiClient.get(`/config/${key}`);
      const config = response.data?.data;
      
      if (config && config.is_active) {
        // Cache the result
        this.configCache.set(key, config.config_value);
        this.cacheExpiry.set(key, Date.now() + this.CACHE_DURATION);
        
        return config.config_value;
      }
      
      return null;
    } catch (error) {
      console.error(`Error fetching config ${key}:`, error);
      return null;
    }
  }

  /**
   * Get referral configuration
   */
  async getReferralConfig(): Promise<ReferralConfig> {
    try {
      const [creditAmount, discountAmount] = await Promise.all([
        this.getConfig<PricingConfig>('referral_credit_amount'),
        this.getConfig<PricingConfig>('referral_discount_amount')
      ]);

      return {
        credit_amount: creditAmount || { thb: 100, usd: 3.00 },
        discount_amount: discountAmount || { thb: 100, usd: 3.00 }
      };
    } catch (error) {
      console.error('Error getting referral config:', error);
      // Return default values as fallback
      return {
        credit_amount: { thb: 100, usd: 3.00 },
        discount_amount: { thb: 100, usd: 3.00 }
      };
    }
  }

  /**
   * Get crypto pricing configuration
   */
  async getCryptoPricing(serviceType: ServiceType): Promise<CryptoPricingConfig> {
    try {
      const key = `crypto_pricing_${serviceType}`;
      const pricing = await this.getConfig<CryptoPricingConfig>(key);
      
      if (pricing) {
        return pricing;
      }

      // Return default crypto pricing as fallback
      if (serviceType === 'flower') {
        return {
          standard: { usd: 9.99 },
          premium: { usd: 15.99 }
        };
      } else if (serviceType === 'fruit') {
        return {
          standard: { usd: 27.99 },
          premium: { usd: 39.99 }
        };
      } else if (serviceType === 'cat_grass') {
        return {
          standard: { usd: 7.99 },
          premium: { usd: 11.99 }
        };
      } else {
        return {
          standard: { usd: 27.99 },
          premium: { usd: 39.99 }
        };
      }
    } catch (error) {
      console.error(`Error getting crypto pricing for ${serviceType}:`, error);
      // Return default values
      if (serviceType === 'flower') {
        return {
          standard: { usd: 9.99 },
          premium: { usd: 15.99 }
        };
      } else {
        return {
          standard: { usd: 27.99 },
          premium: { usd: 39.99 }
        };
      }
    }
  }

  /**
   * Get fallback pricing configuration (emergency use only)
   */
  async getFallbackPricing(serviceType: ServiceType): Promise<FallbackPricingConfig> {
    try {
      const key = `fallback_prices_${serviceType}`;
      const pricing = await this.getConfig<FallbackPricingConfig>(key);
      
      if (pricing) {
        return pricing;
      }

      // Return minimal emergency fallbacks
      if (serviceType === 'flower') {
        return {
          standard: { thb: 499, usd: 14.99 },
          premium: { thb: 699, usd: 19.99 }
        };
      } else if (serviceType === 'fruit') {
        return {
          standard: { thb: 999, usd: 32.99 },
          premium: { thb: 1499, usd: 49.99 }
        };
      } else if (serviceType === 'cat_grass') {
        return {
          standard: { thb: 249, usd: 7.99 },
          premium: { thb: 349, usd: 11.99 }
        };
      } else {
        return {
          standard: { thb: 999, usd: 32.99 },
          premium: { thb: 1499, usd: 49.99 }
        };
      }
    } catch (error) {
      console.error(`Error getting fallback pricing for ${serviceType}:`, error);
      // Return absolute minimal fallbacks
      if (serviceType === 'flower') {
        return {
          standard: { thb: 399, usd: 12.99 },
          premium: { thb: 599, usd: 19.99 }
        };
      } else {
        return {
          standard: { thb: 999, usd: 32.99 },
          premium: { thb: 1499, usd: 49.99 }
        };
      }
    }
  }

  /**
   * Get trial pricing configuration
   */
  async getTrialPricing(serviceType: ServiceType): Promise<FallbackPricingConfig> {
    try {
      const key = `trial_prices_${serviceType}`;
      const pricing = await this.getConfig<FallbackPricingConfig>(key);
      
      if (pricing) {
        return pricing;
      }

      // Return default trial pricing
      if (serviceType === 'flower') {
        return {
          standard: { thb: 199, usd: 5.99 },
          premium: { thb: 299, usd: 8.99 }
        };
      } else if (serviceType === 'fruit') {
        return {
          standard: { thb: 299, usd: 8.99 },
          premium: { thb: 449, usd: 13.99 }
        };
      } else if (serviceType === 'cat_grass') {
        return {
          standard: { thb: 99, usd: 2.99 },
          premium: { thb: 149, usd: 4.99 }
        };
      } else {
        return {
          standard: { thb: 299, usd: 8.99 },
          premium: { thb: 449, usd: 13.99 }
        };
      }
    } catch (error) {
      console.error(`Error getting trial pricing for ${serviceType}:`, error);
      // Return default values
      if (serviceType === 'flower') {
        return {
          standard: { thb: 199, usd: 5.99 },
          premium: { thb: 299, usd: 8.99 }
        };
      } else if (serviceType === 'fruit') {
        return {
          standard: { thb: 299, usd: 8.99 },
          premium: { thb: 449, usd: 13.99 }
        };
      } else if (serviceType === 'cat_grass') {
        return {
          standard: { thb: 99, usd: 2.99 },
          premium: { thb: 149, usd: 4.99 }
        };
      } else {
        return {
          standard: { thb: 299, usd: 8.99 },
          premium: { thb: 449, usd: 13.99 }
        };
      }
    }
  }

  /**
   * Get renewal base pricing
   */
  async getRenewalBasePricing(): Promise<FallbackPricingConfig> {
    try {
      const pricing = await this.getConfig<FallbackPricingConfig>('renewal_base_pricing');
      
      if (pricing) {
        return pricing;
      }

      // Return default renewal pricing
      return {
        standard: { thb: 999, usd: 29.99 },
        premium: { thb: 999, usd: 29.99 } // Same base for both, multiplied by options
      };
    } catch (error) {
      console.error('Error getting renewal base pricing:', error);
      return {
        standard: { thb: 999, usd: 29.99 },
        premium: { thb: 999, usd: 29.99 }
      };
    }
  }

  /**
   * Clear configuration cache
   */
  clearCache(): void {
    this.configCache.clear();
    this.cacheExpiry.clear();
  }

  /**
   * Clear specific configuration from cache
   */
  clearConfigCache(key: string): void {
    this.configCache.delete(key);
    this.cacheExpiry.delete(key);
  }

  /**
   * Get original regular pricing (for showing crossed-out prices)
   */
  async getOriginalRegularPricing(serviceType: ServiceType): Promise<FallbackPricingConfig> {
    try {
      const key = `original_regular_prices_${serviceType}`;
      const pricing = await this.getConfig<FallbackPricingConfig>(key);
      
      if (pricing) {
        return pricing;
      }

      // Return default original regular pricing
      if (serviceType === 'flower') {
        return {
          standard: { thb: 399, usd: 19.99 },
          premium: { thb: 599, usd: 24.99 }
        };
      } else if (serviceType === 'fruit') {
        return {
          standard: { thb: 999, usd: 32.99 },
          premium: { thb: 1299, usd: 42.99 }
        };
      } else if (serviceType === 'cat_grass') {
        return {
          standard: { thb: 299, usd: 9.99 },
          premium: { thb: 449, usd: 14.99 }
        };
      } else {
        return {
          standard: { thb: 999, usd: 32.99 },
          premium: { thb: 1299, usd: 42.99 }
        };
      }
    } catch (error) {
      console.error(`Error getting original regular pricing for ${serviceType}:`, error);
      // Return default values
      if (serviceType === 'flower') {
        return {
          standard: { thb: 399, usd: 19.99 },
          premium: { thb: 599, usd: 24.99 }
        };
      } else if (serviceType === 'fruit') {
        return {
          standard: { thb: 999, usd: 32.99 },
          premium: { thb: 1299, usd: 42.99 }
        };
      } else if (serviceType === 'cat_grass') {
        return {
          standard: { thb: 299, usd: 9.99 },
          premium: { thb: 449, usd: 14.99 }
        };
      } else {
        return {
          standard: { thb: 999, usd: 32.99 },
          premium: { thb: 1299, usd: 42.99 }
        };
      }
    }
  }

  /**
   * Get trial original pricing (for showing what trial compares to)
   */
  async getTrialOriginalPricing(serviceType: ServiceType): Promise<FallbackPricingConfig> {
    try {
      const key = `trial_original_prices_${serviceType}`;
      const pricing = await this.getConfig<FallbackPricingConfig>(key);
      
      if (pricing) {
        return pricing;
      }

      // Return default trial original pricing
      if (serviceType === 'flower') {
        return {
          standard: { thb: 149, usd: 5.99 },
          premium: { thb: 249, usd: 7.99 }
        };
      } else if (serviceType === 'fruit') {
        return {
          standard: { thb: 299, usd: 10.99 },
          premium: { thb: 549, usd: 15.99 }
        };
      } else if (serviceType === 'cat_grass') {
        return {
          standard: { thb: 149, usd: 4.99 },
          premium: { thb: 199, usd: 6.99 }
        };
      } else {
        return {
          standard: { thb: 299, usd: 10.99 },
          premium: { thb: 549, usd: 15.99 }
        };
      }
    } catch (error) {
      console.error(`Error getting trial original pricing for ${serviceType}:`, error);
      // Return default values
      if (serviceType === 'flower') {
        return {
          standard: { thb: 149, usd: 5.99 },
          premium: { thb: 249, usd: 7.99 }
        };
      } else if (serviceType === 'fruit') {
        return {
          standard: { thb: 299, usd: 10.99 },
          premium: { thb: 549, usd: 15.99 }
        };
      } else if (serviceType === 'cat_grass') {
        return {
          standard: { thb: 149, usd: 4.99 },
          premium: { thb: 199, usd: 6.99 }
        };
      } else {
        return {
          standard: { thb: 299, usd: 10.99 },
          premium: { thb: 549, usd: 15.99 }
        };
      }
    }
  }

  /**
   * Preload common configurations
   */
  async preloadConfigs(): Promise<void> {
    try {
      await Promise.all([
        this.getReferralConfig(),
        this.getCryptoPricing('flower'),
        this.getCryptoPricing('fruit'),
        this.getCryptoPricing('cat_grass'),
        this.getRenewalBasePricing(),
        this.getOriginalRegularPricing('flower'),
        this.getOriginalRegularPricing('fruit'),
        this.getOriginalRegularPricing('cat_grass'),
        this.getTrialOriginalPricing('flower'),
        this.getTrialOriginalPricing('fruit'),
        this.getTrialOriginalPricing('cat_grass'),
        this.getTrialPricing('flower'),
        this.getTrialPricing('fruit'),
        this.getTrialPricing('cat_grass')
      ]);
    } catch (error) {
      console.error('Error preloading configs:', error);
    }
  }
}

export const configService = new ConfigService();
export default configService; 